import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';

type SignatoryCandidate = {
  user_id: number;
  display_name: string;
  email: string;
  mobile: string;
};

type Signatory = {
  user_id: number;
  firstname: string;
  lastname: string;
  email: string;
  mobile: string;
  complete: boolean;
};

type SignatoryResponse = {
  data?: {
    signatory: Signatory | null;
    candidates?: SignatoryCandidate[];
  };
};

type CompanySignatoryPanelProps = {
  companyId: number;
  bootstrapKey: string;
};

const fullName = (signatory: Signatory): string =>
  `${signatory.firstname} ${signatory.lastname}`.trim();

/**
 * Designate which of the company's own users co-signs its VS report on a JeSignExpert collecte.
 *
 * The mobile lives here rather than on the user profile because it exists for one purpose: the
 * signature ceremony sends its one-time code to it. A designation without a mobile is kept and
 * shown as unusable rather than refused, so the cabinet sees exactly what is missing.
 */
const CompanySignatoryPanel = ({ companyId, bootstrapKey }: CompanySignatoryPanelProps) => {
  const endpoint = `companies/${companyId}/signatory`;

  const [signatory, setSignatory] = useState<Signatory | null>(null);
  const [candidates, setCandidates] = useState<SignatoryCandidate[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [mobile, setMobile] = useState('');
  const [state, setState] = useState<'loading' | 'idle' | 'saving'>('loading');
  const [error, setError] = useState('');

  const applyResponse = useCallback((response: SignatoryResponse) => {
    const current = response.data?.signatory ?? null;
    setSignatory(current);
    if (response.data?.candidates !== undefined) {
      setCandidates(response.data.candidates);
    }
    setSelectedUserId(current !== null ? String(current.user_id) : '');
    setMobile(current?.mobile ?? '');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiRequest<SignatoryResponse>(endpoint, { method: 'GET' }, bootstrapKey);
        if (!cancelled) {
          applyResponse(response);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : __('Impossible de charger le signataire.'));
        }
      } finally {
        if (!cancelled) {
          setState('idle');
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [applyResponse, bootstrapKey, endpoint]);

  const handleSave = async () => {
    const userId = parseInt(selectedUserId, 10);
    if (!Number.isFinite(userId) || userId <= 0) {
      setError(__('Choisissez un signataire.'));
      return;
    }

    setState('saving');
    setError('');

    try {
      const payload: { user_id: number; mobile?: string } = { user_id: userId };
      if (mobile.trim() !== '') {
        payload.mobile = mobile.trim();
      }

      const response = await apiRequest<SignatoryResponse>(
        endpoint,
        { method: 'PUT', body: JSON.stringify(payload) },
        bootstrapKey,
      );
      applyResponse(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : __('Enregistrement impossible.'));
    } finally {
      setState('idle');
    }
  };

  const handleClear = async () => {
    setState('saving');
    setError('');

    try {
      await apiRequest<SignatoryResponse>(endpoint, { method: 'DELETE' }, bootstrapKey);
      setSignatory(null);
      setSelectedUserId('');
      setMobile('');
    } catch (e) {
      setError(e instanceof Error ? e.message : __('Suppression impossible.'));
    } finally {
      setState('idle');
    }
  };

  if (state === 'loading') {
    return <p>{__('Chargement du signataire...')}</p>;
  }

  const statusLine =
    signatory === null
      ? __('Aucun signataire désigné pour cette entreprise.')
      : signatory.complete
        ? `${fullName(signatory)} — ${signatory.email}`
        : `${fullName(signatory)} — ${__('mobile manquant, ce signataire ne peut pas être invité à signer.')}`;

  return (
    <section className="company-signatory" aria-label={__('Signataire de l’entreprise')}>
      <p className="company-signatory__status">{statusLine}</p>

      {candidates.length === 0 ? (
        <p className="company-signatory__hint">
          {__('Rattachez d’abord un utilisateur à cette entreprise pour pouvoir le désigner.')}
        </p>
      ) : (
        <>
          <label htmlFor="company-signatory-user">{__('Signataire')}</label>
          <select
            id="company-signatory-user"
            value={selectedUserId}
            disabled={state === 'saving'}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              setSelectedUserId(event.target.value);
              const picked = candidates.find((c) => String(c.user_id) === event.target.value);
              setMobile(picked?.mobile ?? '');
            }}
          >
            <option value="">{__('— Choisir —')}</option>
            {candidates.map((candidate) => (
              <option key={candidate.user_id} value={candidate.user_id}>
                {candidate.display_name} ({candidate.email})
              </option>
            ))}
          </select>

          <label htmlFor="company-signatory-mobile">{__('Mobile')}</label>
          <input
            id="company-signatory-mobile"
            type="tel"
            value={mobile}
            disabled={state === 'saving'}
            placeholder="+33612345678"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setMobile(event.target.value); }}
          />

          <div className="company-signatory__actions">
            <button
              type="button"
              className="btn-primary"
              disabled={state === 'saving'}
              onClick={() => { void handleSave(); }}
            >
              {state === 'saving' ? __('Enregistrement…') : __('Enregistrer')}
            </button>
            {signatory !== null ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={state === 'saving'}
                onClick={() => { void handleClear(); }}
              >
                {__('Retirer')}
              </button>
            ) : null}
          </div>
        </>
      )}

      {error !== '' ? (
        <p className="company-signatory__error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};

export default CompanySignatoryPanel;
